import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

const FIELDS = ['primary_color', 'secondary_color', 'accent_color', 'sidebar_color', 'page_bg_color', 'card_bg_color', 'button_color'];
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PresetTile = ({ preset, onApply, onDelete }) => (
  <div className="relative group" data-testid={`saved-preset-${preset.id}`}>
    <button
      type="button"
      onClick={() => onApply(preset)}
      className="w-full text-left p-2.5 rounded-lg bg-white border border-stone-200 hover:border-emerald-400 hover:shadow-md transition"
      data-testid={`saved-preset-apply-${preset.id}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className="flex -space-x-1">
          {[preset.colors.sidebar_color, preset.colors.primary_color, preset.colors.page_bg_color].filter(Boolean).map((c, i) => (
            <div key={i} className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: c }} />
          ))}
        </div>
        <div className="text-sm font-bold text-stone-900 leading-tight pr-4 break-words">{preset.name}</div>
      </div>
      <div className="text-[10px] text-stone-500 leading-tight">Saved preset</div>
    </button>
    <button
      type="button"
      onClick={() => onDelete(preset)}
      className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white border border-stone-200 text-stone-500 hover:bg-red-50 hover:text-red-600 hover:border-red-300 flex items-center justify-center transition"
      title="Delete preset"
      data-testid={`saved-preset-delete-${preset.id}`}
    ><X size={11} /></button>
  </div>
);

export const SavedThemePresets = ({ settings, onApply }) => {
  const [presets, setPresets] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/organization/theme-presets`, { headers: headers() })
      .then((r) => setPresets(r.data || []))
      .catch(() => setPresets([]));
  }, []);

  const save = async () => {
    if (!name.trim()) return toast.error('Give your preset a name');
    setSaving(true);
    try {
      const colors = Object.fromEntries(FIELDS.filter((f) => settings[f]).map((f) => [f, settings[f]]));
      const r = await axios.post(`${API}/organization/theme-presets`, { name: name.trim(), colors }, { headers: headers() });
      setPresets((p) => [...p, r.data]);
      toast.success(`Preset "${r.data.name}" saved`);
      setOpen(false);
      setName('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save preset');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (preset) => {
    try {
      await axios.delete(`${API}/organization/theme-presets/${preset.id}`, { headers: headers() });
      setPresets((p) => p.filter((x) => x.id !== preset.id));
      toast.success(`Preset "${preset.name}" deleted`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete preset');
    }
  };

  const apply = (preset) => {
    onApply(preset.colors);
    toast.info(`${preset.name} palette loaded — click Save Changes to apply.`);
  };

  return (
    <div className="mt-3" data-testid="saved-theme-presets">
      {presets.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
          {presets.map((p) => <PresetTile key={p.id} preset={p} onApply={apply} onDelete={remove} />)}
        </div>
      )}
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} className="rounded-full" data-testid="save-as-preset-btn">
        <Plus size={14} className="mr-1" /> Save as Preset
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="save-preset-modal">
          <DialogHeader><DialogTitle>Save current theme as preset</DialogTitle><DialogDescription>Stores all brand and surface colours under this name.</DialogDescription></DialogHeader>
          <Input
            autoFocus
            value={name}
            maxLength={40}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && save()}
            placeholder="e.g. Bella Casa Dark"
            data-testid="save-preset-name-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="save-preset-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-preset-confirm-btn">{saving ? 'Saving…' : 'Save Preset'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
